import { Watch } from '@mlb-streaming/moq-playback';
import { useSignal, useSinglePlayer, useValue } from '@mlb-streaming/moq-playback/react';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { Input } from '@optools/components/ui/input';
import { cn } from '@optools/components/ui/utils';
import { Maximize, Minimize, Pause, Play, Volume2, VolumeOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export interface MoqPlayerProps {
  broadcastName: string;
  className?: string;
  relayUrl: string;
}

interface PlaybackError {
  broadcastName: string;
  kind: 'broadcast-unavailable' | 'invalid-relay' | 'relay-unreachable';
  relayUrl: string;
}

const playbackErrorMessages: Record<PlaybackError['kind'], string> = {
  'broadcast-unavailable': 'Broadcast unavailable. Still waiting…',
  'invalid-relay': 'Invalid relay URL.',
  'relay-unreachable': 'Relay unreachable. Retrying…',
};

// Keep fallback signals stable across renders for useValue.
const connectionEstablishedFallback = new Watch.Signals.Signal(undefined);
const broadcastStatusFallback = new Watch.Signals.Signal('offline');

export function MoqPlayer(props: MoqPlayerProps) {
  // The library probes audio support before initializing its audio polyfill.
  if (typeof window.VideoDecoder !== 'function' || typeof window.AudioDecoder !== 'function') {
    return (
      <Card className={cn('flex aspect-video items-center justify-center p-4', props.className)}>
        <p className="text-center text-sm text-muted-foreground" role="alert">
          MoQ playback requires a browser with WebCodecs audio and video decoding support.
        </p>
      </Card>
    );
  }

  return <MoqPlayback {...props} />;
}

function MoqPlayback({ broadcastName, className, relayUrl }: MoqPlayerProps) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<PlaybackError | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Undefined on iPhone Safari and false in iframes without allow="fullscreen".
  const fullscreenEnabled = Boolean(document.fullscreenEnabled);
  const containerRef = useRef<HTMLDivElement>(null);
  const reportPlaybackError = (kind: PlaybackError['kind']) => {
    setError((currentError) => {
      if (
        kind !== 'invalid-relay' &&
        currentError?.kind === 'invalid-relay' &&
        currentError.relayUrl === relayUrl
      ) {
        return currentError;
      }

      return { broadcastName, kind, relayUrl };
    });
  };
  const player = useSinglePlayer({
    broadcastName,
    canvas,
    onBroadcastUnavailable: () => reportPlaybackError('broadcast-unavailable'),
    onInvalidRelayUrl: () => reportPlaybackError('invalid-relay'),
    onRelayUnreachable: () => reportPlaybackError('relay-unreachable'),
    relayUrl,
  });
  const connected = Boolean(
    useValue(player.connection?.established ?? connectionEstablishedFallback),
  );
  const status = useValue(player.broadcast?.out.status ?? broadcastStatusFallback);
  const [muted, setMuted] = useSignal(player.muted);
  const [paused, setPaused] = useSignal(player.paused);
  const [volume, setVolume] = useSignal(player.volume);

  // The library reports errors through callbacks, but recovery must be inferred from signals.
  useEffect(() => {
    setError((currentError) => {
      if (!currentError) {
        return null;
      }

      const sourceChanged =
        currentError.relayUrl !== relayUrl ||
        (currentError.kind === 'broadcast-unavailable' &&
          currentError.broadcastName !== broadcastName);
      const recovered =
        (currentError.kind === 'relay-unreachable' && connected) ||
        (currentError.kind === 'broadcast-unavailable' && connected && status === 'live');

      return sourceChanged || recovered ? null : currentError;
    });
  }, [broadcastName, connected, error, relayUrl, status]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleFullscreen = async () => {
    if (isFullscreen) {
      // Per spec this only rejects when nothing is fullscreen, which isFullscreen rules out.
      await document.exitFullscreen();
      return;
    }
    try {
      await containerRef.current?.requestFullscreen();
    } catch {
      toast.error('Fullscreen Error', { description: 'Unable to enter fullscreen.' });
    }
  };

  const errorMessage =
    error?.relayUrl === relayUrl &&
    (error.kind !== 'broadcast-unavailable' || error.broadcastName === broadcastName)
      ? playbackErrorMessages[error.kind]
      : null;
  const statusMessage =
    errorMessage ??
    (!connected
      ? 'Connecting to relay…'
      : status === 'live'
        ? paused
          ? 'Paused'
          : 'Live'
        : status === 'loading'
          ? 'Loading broadcast…'
          : 'Waiting for broadcast…');

  return (
    <Card ref={containerRef} className={cn('flex flex-col gap-0 overflow-hidden p-0', className)}>
      <div
        className={cn(
          'relative flex aspect-video min-h-0 items-center justify-center bg-black',
          isFullscreen && 'aspect-auto flex-1',
        )}>
        <canvas
          ref={setCanvas}
          aria-label={`MoQ playback for ${broadcastName}`}
          className="absolute inset-0 h-full w-full object-contain"
          data-testid="moq-player"
          role="img"
        />
        {(errorMessage !== null || status !== 'live') && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-muted p-4"
            data-testid="moq-player-overlay">
            <p
              className={cn(
                'text-center text-sm',
                errorMessage ? 'text-destructive' : 'text-muted-foreground',
              )}>
              {statusMessage}
            </p>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 border-t border-border p-2">
        <Button
          aria-label={paused ? 'Play' : 'Pause'}
          size="icon-sm"
          title={paused ? 'Play' : 'Pause'}
          type="button"
          variant="ghost"
          onClick={() => setPaused(!paused)}>
          {paused ? <Play /> : <Pause />}
        </Button>
        <Button
          aria-label={muted ? 'Unmute' : 'Mute'}
          size="icon-sm"
          title={muted ? 'Unmute' : 'Mute'}
          type="button"
          variant="ghost"
          onClick={() => setMuted(!muted)}>
          {muted ? <VolumeOff /> : <Volume2 />}
        </Button>
        <Input
          aria-label="Volume"
          className="h-6 w-24 border-0 p-0 accent-primary shadow-none"
          max={1}
          min={0}
          step={0.01}
          type="range"
          value={volume}
          onChange={(event) => setVolume(Number(event.target.value))}
        />
        <span className="sr-only" role="status">
          {statusMessage}
        </span>
        {fullscreenEnabled && (
          <Button
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="ml-auto"
            size="icon-sm"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            type="button"
            variant="ghost"
            onClick={() => void handleFullscreen()}>
            {isFullscreen ? <Minimize /> : <Maximize />}
          </Button>
        )}
      </div>
    </Card>
  );
}
